import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import usePageContent from '../hooks/usePageContent';
import { fetchCMSCollection } from '../lib/cms';
import { fallbackPrices, businessInfo } from '../data';

export default function Prices() {
  const { page, loading: pageLoading } = usePageContent('priser');
  const [prices, setPrices] = useState([]);
  const [pricesLoading, setPricesLoading] = useState(true);

  useEffect(() => {
    if (page) {
      document.title = page.title;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', page.meta_description);
      }
    }
  }, [page]);

  useEffect(() => {
    async function loadPrices() {
      try {
        const data = await fetchCMSCollection('price_items');
        if (data && data.items && data.items.length > 0) {
          // Sort prices by price or title if needed
          const sorted = [...data.items].sort((a, b) => b.price - a.price);
          setPrices(sorted);
        } else {
          setPrices(fallbackPrices);
        }
      } catch (error) {
        console.error('Failed to load prices from CMS, using fallback:', error);
        setPrices(fallbackPrices);
      } finally {
        setPricesLoading(false);
      }
    }
    loadPrices();
  }, []);

  if (pageLoading || pricesLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-brand-beige">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-sage border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="bg-brand-beige">
      {/* Page Header */}
      <section className="bg-brand-cream/30 py-12 md:py-16 border-b border-brand-sage-medium/10">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-sage-dark">
            Priser
          </span>
          <h1 className="mt-2 font-serif text-3xl font-semibold text-brand-neutral-dark md:text-4xl lg:text-5xl">
            {page.hero_title}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-brand-neutral-dark/80 md:text-base">
            {page.hero_subtitle}
          </p>
        </div>
      </section>

      {/* Pricing List Section */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-6">
          
          {/* Intro Content */}
          <div 
            className="prose prose-stone text-sm leading-relaxed text-brand-neutral-dark/80 mb-12 text-center max-w-3xl mx-auto"
            dangerouslySetInnerHTML={{ __html: page.content }}
          />

          {/* Pricing Cards/List */}
          <div className="flex flex-col gap-6">
            {prices
              .filter(item => {
                const title = (item.title?.da || item.title || '').toLowerCase();
                return !title.includes('børnebehandling') && !title.includes('0-12');
              })
              .map((item, index) => {
                const title = item.title?.da || item.title;
                const duration = item.duration?.da || item.duration;
                const desc = item.description?.da || item.description;
                const price = item.price;

                return (
                  <div 
                    key={item.id || index}
                    className="rounded-2xl border border-brand-sage-medium/15 bg-white/60 p-6 md:p-8 shadow-sm transition-all duration-300 hover:shadow-md flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                  >
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3">
                        <h3 className="font-serif text-lg font-semibold text-brand-neutral-dark md:text-xl">
                          {title}
                        </h3>
                        <span className="inline-flex items-center gap-1 rounded-full bg-brand-sage-light px-2.5 py-0.5 text-xs font-medium text-brand-sage-dark">
                          <i className="fa-light fa-clock" /> {duration}
                        </span>
                      </div>
                      {desc && (
                        <p className="mt-2 text-xs leading-relaxed text-brand-neutral-dark/80 max-w-xl">
                          {desc}
                        </p>
                      )}
                    </div>
                    <div className="flex items-baseline gap-1 shrink-0 border-t border-brand-sage-medium/10 pt-4 md:border-t-0 md:pt-0">
                      {price === 0 ? (
                        <span className="text-2xl font-bold text-brand-sage-dark">Gratis</span>
                      ) : (
                        <>
                          <span className="text-2xl font-bold text-brand-neutral-dark">{price}</span>
                          <span className="text-xs text-brand-neutral-light">kr.</span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-brand-cream/30 py-12 border-t border-brand-sage-medium/10">
        <div className="mx-auto max-w-4xl px-6 text-center flex flex-col items-center gap-4">
          <h2 className="font-serif text-2xl font-semibold text-brand-neutral-dark">
            Klar til at bestille din tid?
          </h2>
          <p className="text-sm text-brand-neutral-dark/80 max-w-xl">
            Du kan nemt kontakte mig for at aftale en tid, der passer dig.
          </p>
          <div className="mt-2 flex flex-wrap justify-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center rounded-full bg-brand-sage px-6 py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-300 hover:bg-brand-sage-dark"
            >
              Gå til kontaktformular
            </Link>
            <a
              href={`tel:+45${businessInfo.phone.replace(/\s/g, '')}`}
              className="inline-flex items-center justify-center rounded-full border border-brand-sage-medium/30 bg-white/40 px-6 py-2.5 text-sm font-medium text-brand-neutral-dark transition-all duration-300 hover:bg-white/80"
            >
              Ring <span className="whitespace-nowrap ml-1">{businessInfo.phone}</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
